<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  affiliateList,
  affiliateDelete,
  affiliateCreate,
  affiliateUpdate,
} from "@/common/api/rest/affiliate";

import type {
  AffiliateType,
  AffiliatePayloadType,
} from "@/common/types/affiliate";

import { RegisterTheme } from "@/common/themes";
import { technicalForm } from "@/components";
import {
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t, tm } = useI18n();

const route = useRoute();

const tableHeader = [
  { key: "name", label: "Nome" },
  { key: "email", label: "Email" },
  { key: "type", label: "Tipo" },
  { key: "status", label: "Status" },
];

const accountId = computed(() => route.params.id as string);

const tableData = ref<AffiliateType[]>([]);
const limited = ref(false);
const isEdit = ref(false);

const selected = ref<AffiliateType | null>(null);
const payload = ref<AffiliatePayloadType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showConfirmDel = ref(false);
const showModal = ref(false);

const types = computed(() => tm("technical.types") as Record<string, string>);

function checkLimit() {
  // affiliateLimit(accountId.value).then((response) => {
  //   const { success, data, message } = response;
  //   if (success) {
  //     limited.value = data.limited;
  //   } else {
  //     toast({ message: message, type: "error" });
  //   }
  // });
}

function fetchTechnicals() {
  affiliateList(accountId.value, {
    page: page.value,
    limit: limit.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function removeItem(data: AffiliateType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function confirmRemoveItem() {
  if (!selected.value) return;

  affiliateDelete(accountId.value, selected.value.affiliate_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        fetchTechnicals();
        checkLimit();
      } else {
        toast({ message: message, type: "error" });
      }
      cancelRemoveItem();
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
      cancelRemoveItem();
    });
}

function cancelRemoveItem() {
  selected.value = null;
  showConfirmDel.value = false;
}

function openModal(data?: AffiliateType) {
  if (data) {
    selected.value = data;
    isEdit.value = true;
    showModal.value = true;
    return;
  }

  if (limited.value) {
    toast({ message: t("technical.texts.technical_limit"), type: "error" });
    return;
  }

  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  isEdit.value = false;
  selected.value = null;
}

function addTechnical() {
  if (limited.value) {
    toast({ message: t("technical.texts.technical_limit"), type: "error" });
    return;
  }

  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  affiliateCreate(accountId.value, payload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        closeModal();
        fetchTechnicals();
        checkLimit();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editTechnical() {
  if (!selected.value) return;

  affiliateUpdate(
    accountId.value,
    selected.value.affiliate_id,
    payload.value as AffiliatePayloadType,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        closeModal();
        fetchTechnicals();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function submitPayload() {
  if (isEdit.value) {
    editTechnical();
  } else {
    addTechnical();
  }
}

function searchItem(value: string) {
  search.value = value;
  fetchTechnicals();
}

function changePage(value: number) {
  page.value = value;
  fetchTechnicals();
}

onMounted(() => {
  if (accountId.value) {
    fetchTechnicals();
    checkLimit();
  }
});
</script>

<template>
  <register-theme tab-active="technical">
    <template #title>
      <svg-icon icon="technical" size="2rem" />
      AFILIADOS
    </template>

    <template #content>
      <base-table
        :header="tableHeader"
        :data="tableData"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :edit="true"
        :del="true"
        @change:page="changePage"
        @searching="searchItem"
        @btn:add="openModal()"
        @btn:edit="openModal"
        @btn:del="removeItem"
      >
        <template #name="{ data }">
          {{ data.name }}
        </template>

        <template #type="{ data }">
          {{ types[data.type] }}
        </template>

        <template #status="{ data }">
          {{ t(`common.labels.${data.status.toLowerCase()}`) }}
        </template>
      </base-table>
    </template>
  </register-theme>

  <base-modal
    :show="showModal"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.save')"
    :button-close="t('common.labels.cancel')"
    @close="closeModal()"
    @confirm="submitPayload()"
  >
    <template #title>
      <div
        class="flex flex-row gap-1 items-center text-6 font-bold uppercase mb-4"
      >
        <svg-icon icon="technical" size="2.5rem" />
        {{ t("technical.texts.add_technical") }}
      </div>
    </template>

    <template #content>
      <technical-form :data="selected" @update-data="payload = $event" />
    </template>
  </base-modal>

  <base-confirm
    :show="showConfirmDel"
    :title="
      t('common.labels.remove', {
        concat: t('common.labels.technical'),
      }).toUpperCase()
    "
    :message="
      t('common.texts.remove_message', {
        concat: selected?.name,
      })
    "
    @cancel="cancelRemoveItem"
    @confirm="confirmRemoveItem"
  />
</template>
